export const LANDING_PAGES = [
  {
    slug: "anxiety-therapy",
    title: "Anxiety Therapy",
    headline: ["Online Anxiety Therapy", "Find Your Path to Calm"],
    metaDescription:
      "Struggling with anxiety? Our licensed therapists offer professional online anxiety therapy in the UK. Book your first session today.",
    heroSubtitle:
      "You don't have to live with constant worry, fear, or panic. Our experienced therapists are here to help you understand your anxiety and build lasting calm.",
    heroImage: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=1600&q=80",
    sectionImage: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=800&q=80",
    color: "#625480",
    whatIs: {
      heading: "What Is Anxiety?",
      body: [
        "Anxiety is one of the most common mental health challenges in the world, yet it is often misunderstood. At its core, anxiety is your body's natural alarm system, a response to perceived threats or danger. For most people, this response fades once the threat passes. But for millions of people, anxiety becomes persistent, overwhelming, and difficult to control.",
        "Anxiety disorders come in many forms: generalised anxiety disorder (GAD), social anxiety, panic disorder, health anxiety, and phobias. While each type presents differently, they share a common thread: excessive, disproportionate fear or worry that interferes with daily life.",
        "Living with untreated anxiety can affect your relationships, career, physical health, and overall quality of life. The good news is that anxiety is highly treatable, and therapy is one of the most effective ways to manage it long-term.",
      ],
    },
    symptoms: {
      heading: "Signs You May Be Experiencing Anxiety",
      items: [
        "Persistent worry or fear that feels impossible to control",
        "Racing thoughts, especially at night",
        "Physical symptoms such as a racing heart, sweating, or shortness of breath",
        "Avoiding situations that trigger anxiety",
        "Difficulty concentrating or making decisions",
        "Irritability or restlessness",
        "Muscle tension, headaches, or stomach problems",
        "Panic attacks (sudden episodes of intense fear)",
      ],
    },
    howWeHelp: {
      heading: "How We Help with Anxiety Therapy",
      body: [
        "At MindTherapy Counselling, our therapists use evidence-based approaches to help you understand and manage anxiety. We tailor treatment to your specific needs, history, and goals.",
        "Cognitive Behavioural Therapy (CBT) is one of the most widely researched and effective treatments for anxiety. It helps you identify the thought patterns that fuel anxiety and replace them with more balanced, realistic thinking. Over time, you learn to respond differently to anxious thoughts, reducing their power over you.",
        "We also offer mindfulness-based approaches, acceptance and commitment therapy (ACT), and exposure therapy for phobias and panic. Your therapist will work with you to choose the right combination of techniques for your situation.",
        "Sessions are held online, meaning you can access support from the comfort of your own home, which is especially helpful when anxiety makes going out feel difficult.",
      ],
    },
    faq: [
      {
        q: "Can therapy really help with anxiety?",
        a: "Yes. Research consistently shows that therapy, particularly CBT, is highly effective for anxiety disorders. Many people see significant improvement within 8 to 12 sessions.",
      },
      {
        q: "How long will I need to attend therapy?",
        a: "This varies depending on the type and severity of your anxiety. Some people benefit from short-term therapy (6 to 12 sessions), while others prefer ongoing support. Your therapist will discuss this with you in your first session.",
      },
      {
        q: "Is online therapy as effective as in-person therapy for anxiety?",
        a: "Multiple studies confirm that online therapy is equally effective as in-person therapy for anxiety. Many clients actually find it easier to open up from the comfort of their own space.",
      },
      {
        q: "What if I have a panic attack during a session?",
        a: "Our therapists are trained to support you through panic attacks safely. They will guide you through grounding techniques and ensure you feel supported at all times.",
      },
      {
        q: "Do I need a GP referral to start therapy?",
        a: "No referral is needed. You can book your first session directly through our platform.",
      },
    ],
  },
  {
    slug: "depression-therapy",
    title: "Depression Therapy",
    headline: ["Online Depression Therapy", "You Don't Have to Face It Alone"],
    metaDescription:
      "Feeling low, hopeless, or empty? Our licensed therapists offer compassionate online depression therapy in the UK. Book a session today.",
    heroSubtitle:
      "Depression is more than feeling sad. It's a real, treatable condition, and with the right support, recovery is possible. Our therapists are ready to walk alongside you.",
    heroImage: "https://images.unsplash.com/photo-1516534775068-ba3e7458af70?auto=format&fit=crop&w=1600&q=80",
    sectionImage: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80",
    color: "#4a6fa5",
    whatIs: {
      heading: "What Is Depression?",
      body: [
        "Depression is a serious mental health condition that affects how you think, feel, and function in everyday life. It goes far beyond feeling sad or having a bad day. Depression is a persistent state of low mood, hopelessness, and loss of interest that can last for weeks, months, or even years without treatment.",
        "Clinical depression, also known as major depressive disorder, affects approximately 1 in 6 people in the UK at some point in their lives. It can affect anyone regardless of age, background, or circumstance. Depression is not a sign of weakness or personal failure; it is a medical condition with both psychological and biological components.",
        "The impact of untreated depression can be profound. It affects relationships, work, physical health, and your sense of self. But it is also one of the most treatable mental health conditions, and therapy plays a central role in long-term recovery.",
      ],
    },
    symptoms: {
      heading: "Common Signs of Depression",
      items: [
        "Persistent low mood, sadness, or emptiness lasting more than two weeks",
        "Loss of interest or pleasure in activities you once enjoyed",
        "Changes in appetite or weight (increase or decrease)",
        "Sleep disturbances, either sleeping too much or too little",
        "Fatigue and lack of energy, even after rest",
        "Difficulty concentrating, remembering things, or making decisions",
        "Feelings of worthlessness, guilt, or hopelessness",
        "Thoughts of death or suicide (if you are in crisis, please contact a helpline immediately)",
      ],
    },
    howWeHelp: {
      heading: "How Depression Therapy Works",
      body: [
        "At MindTherapy Counselling, we offer compassionate, evidence-based therapy for depression. We understand that reaching out takes courage, and we're committed to creating a safe, non-judgmental space where you can begin to heal.",
        "Cognitive Behavioural Therapy (CBT) is one of the most effective treatments for depression. It helps you identify negative thought patterns such as catastrophising, self-blame, or black-and-white thinking, and gradually replace them with more helpful perspectives. CBT also includes behavioural activation techniques, which help you re-engage with activities and experiences that bring meaning and pleasure.",
        "We also offer psychodynamic therapy, which explores how past experiences and relationships may contribute to your current emotional state. For some people, understanding the roots of depression is an important part of lasting recovery.",
        "Whether you are experiencing mild, moderate, or severe depression, our therapists will collaborate with you on a personalised treatment plan that respects your pace and your goals.",
      ],
    },
    faq: [
      {
        q: "Is therapy enough for depression, or do I also need medication?",
        a: "For mild to moderate depression, therapy alone is often very effective. For severe depression, a combination of therapy and medication is sometimes recommended. Your therapist can discuss this and help you explore options with your GP if needed.",
      },
      {
        q: "I barely have the energy to get through the day. Can I still do therapy online?",
        a: "Absolutely. Online therapy is designed to reduce barriers. You can attend from your bed, sofa, or anywhere you feel comfortable. Our therapists are experienced in working with people who are finding it hard to function.",
      },
      {
        q: "How many sessions will I need?",
        a: "It depends on your individual situation. Many people notice improvement after 6 to 8 sessions. Others benefit from longer-term support. Your therapist will review progress with you regularly.",
      },
      {
        q: "What if I'm having thoughts of suicide?",
        a: "If you are in immediate danger, please call 999 or go to your nearest A&E. For emotional support, you can call Samaritans on 116 123. Our therapists can also discuss safety planning with you as part of your treatment.",
      },
      {
        q: "Will I have the same therapist each session?",
        a: "Yes. Consistency is important in depression therapy. You will work with the same therapist throughout your treatment, building a trusting therapeutic relationship.",
      },
    ],
  },
  {
    slug: "couples-therapy",
    title: "Couples Therapy",
    filterBranch: "Family And Couple Therapy",
    headline: ["Online Couples Therapy", "Rebuild Connection, Together"],
    metaDescription:
      "Strengthen your relationship with professional online couples therapy in the UK. Our expert therapists help you communicate better and reconnect. Book today.",
    heroSubtitle:
      "Every relationship faces challenges. Couples therapy gives you and your partner the tools, insights, and shared language to overcome them and grow closer in the process.",
    heroImage: "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?auto=format&fit=crop&w=1600&q=80",
    sectionImage: "https://images.unsplash.com/photo-1516589091380-5d8e87df6999?auto=format&fit=crop&w=800&q=80",
    color: "#7a5c8a",
    whatIs: {
      heading: "What Is Couples Therapy?",
      body: [
        "Couples therapy, also known as relationship counselling, is a form of psychotherapy that helps partners address conflict, improve communication, and strengthen their emotional connection. It provides a structured, safe space where both individuals can express their feelings, be heard, and work collaboratively toward a healthier relationship.",
        "Couples therapy is not just for relationships in crisis. Many couples seek therapy as a proactive step to deepen their connection, navigate a major life transition, or develop better communication before issues become serious. Whether you have been together for months or decades, therapy can offer new perspectives and practical tools.",
        "A skilled couples therapist acts as a neutral guide, not taking sides, but helping both partners understand each other's needs, patterns, and attachment styles. The goal is not simply to resolve arguments, but to build a relationship that is more resilient, empathetic, and fulfilling for both people.",
      ],
    },
    symptoms: {
      heading: "Signs Your Relationship May Benefit from Couples Therapy",
      items: [
        "Recurring arguments that never seem to get resolved",
        "Feeling emotionally disconnected or distant from your partner",
        "Communication has broken down or become hostile",
        "Trust has been damaged through infidelity, dishonesty, or betrayal",
        "Intimacy (physical or emotional) has declined significantly",
        "Major life changes are creating tension (having children, moving, career stress)",
        "One or both partners feel unheard, unseen, or unappreciated",
        "You are considering separation but want to try therapy first",
      ],
    },
    howWeHelp: {
      heading: "Our Approach to Couples Therapy",
      body: [
        "At MindTherapy Counselling, our couples therapists are trained in evidence-based relationship therapy models, including the Gottman Method and Emotionally Focused Therapy (EFT). These approaches have decades of research behind them and are shown to produce meaningful, lasting change in relationships.",
        "The Gottman Method focuses on building friendship, managing conflict constructively, and creating shared meaning. It helps couples identify negative interaction cycles such as criticism, contempt, defensiveness, and stonewalling, and replace them with healthier patterns.",
        "Emotionally Focused Therapy (EFT) works at a deeper level, exploring attachment needs and the emotional bonds that underpin every relationship. It helps partners move from disconnection and conflict toward closeness and secure attachment.",
        "Online sessions make it easy for couples to attend together, even if you work different hours, live in different locations, or simply prefer the privacy of your own home. All you need is a device with a camera and a quiet space.",
      ],
    },
    faq: [
      {
        q: "What if my partner is reluctant to try couples therapy?",
        a: "This is very common. Sometimes starting with individual therapy and sharing what you learn can open a conversation. We also offer initial consultations where both partners can ask questions before committing.",
      },
      {
        q: "Can couples therapy save a relationship?",
        a: "Therapy can significantly improve relationships when both partners are willing to engage. Research shows that EFT, for example, has a success rate of over 70%. However, therapy is also a space to explore whether continuing the relationship is right for both of you.",
      },
      {
        q: "Do we have to be in the same room for online couples therapy?",
        a: "Not necessarily. Some couples attend from the same location; others join from separate rooms or locations. Your therapist can advise on what works best for your situation.",
      },
      {
        q: "How often should we attend sessions?",
        a: "Weekly sessions are recommended, especially at the start. As you progress, sessions may become fortnightly. Your therapist will discuss this with you as you go.",
      },
      {
        q: "Is everything we discuss confidential?",
        a: "Yes. All sessions are strictly confidential. The only exceptions are standard safeguarding situations (risk of serious harm), which your therapist will explain at the outset.",
      },
    ],
  },
  {
    slug: "online-therapy-uk",
    title: "Online Therapy UK",
    headline: ["Online Therapy in the UK", "Professional Support, From Anywhere"],
    metaDescription:
      "Access licensed therapists from anywhere in the UK. Flexible, affordable, and confidential online therapy sessions. Book your first appointment today.",
    heroSubtitle:
      "Quality mental health support shouldn't depend on where you live or how busy you are. Our UK-based therapists offer flexible online sessions that fit around your life.",
    heroImage: "https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?auto=format&fit=crop&w=1600&q=80",
    sectionImage: "https://images.unsplash.com/photo-1591115765373-5207764f72e7?auto=format&fit=crop&w=800&q=80",
    color: "#3d7a6e",
    whatIs: {
      heading: "What Is Online Therapy?",
      body: [
        "Online therapy, sometimes called teletherapy or e-therapy, is professional psychological support delivered via secure video call. It offers the same therapeutic experience as in-person therapy, but with the added convenience and flexibility of attending from home, work, or anywhere with a reliable internet connection.",
        "In the UK, online therapy has grown significantly in recent years, and research consistently shows that it is just as effective as face-to-face therapy for a wide range of mental health concerns, including anxiety, depression, relationship difficulties, trauma, and stress.",
        "MindTherapy Counselling connects you with licensed, accredited therapists across the UK. Whether you're based in London, Manchester, Edinburgh, or a rural area with limited access to in-person services, our platform ensures that quality mental health support is always within reach.",
      ],
    },
    symptoms: {
      heading: "Who Can Benefit from Online Therapy?",
      items: [
        "Anyone experiencing anxiety, depression, stress, or low mood",
        "People with busy schedules who struggle to attend in-person appointments",
        "Those living in areas with limited access to local therapists",
        "Individuals who feel more comfortable opening up from their own environment",
        "People with mobility issues or health conditions that make travel difficult",
        "Couples seeking relationship counselling from separate locations",
        "Anyone who has previously struggled to find a therapist they connect with",
        "Those seeking therapy in their native language",
      ],
    },
    howWeHelp: {
      heading: "Why Choose MindTherapy for Online Therapy in the UK",
      body: [
        "MindTherapy Counselling is a UK-based online therapy platform built around one belief: that everyone deserves access to quality mental health support, regardless of location, background, or circumstance.",
        "Our therapists are fully qualified and accredited by recognised UK bodies, including the BACP (British Association for Counselling and Psychotherapy) and UKCP (UK Council for Psychotherapy). Every therapist on our platform has undergone rigorous vetting to ensure they meet the highest professional and ethical standards.",
        "We offer a multilingual platform, meaning you can access therapy in your preferred language. For many people, being able to speak with a therapist in their mother tongue is essential. It removes an invisible but significant barrier to honest self-expression.",
        "Booking is simple, sessions are flexible, and you can choose a therapist based on their specialism, approach, and availability. We also offer competitive pricing to ensure therapy is as accessible as possible.",
      ],
    },
    faq: [
      {
        q: "Is online therapy regulated in the UK?",
        a: "Yes. All therapists on our platform are accredited by recognised professional bodies (BACP, UKCP, or equivalent). They adhere to the same ethical guidelines as in-person therapists.",
      },
      {
        q: "What technology do I need for online therapy?",
        a: "A smartphone, tablet, or computer with a camera and microphone, and a stable internet connection. We use a secure, GDPR-compliant video platform.",
      },
      {
        q: "Is my data and privacy protected?",
        a: "Absolutely. We are fully GDPR compliant. All video sessions are encrypted end-to-end and no session content is recorded or stored.",
      },
      {
        q: "Can I choose my own therapist?",
        a: "Yes. You can browse therapist profiles, read about their specialisms and approaches, and book directly with the therapist you feel is the right fit for you.",
      },
      {
        q: "What if I need to cancel or reschedule?",
        a: "You can reschedule or cancel sessions through your account. We ask for at least 24 hours notice where possible to avoid a cancellation fee.",
      },
    ],
  },
  {
    slug: "child-therapy",
    title: "Child Therapy",
    filterBranch: "Child Therapy",
    headline: ["Online Child Therapy", "Supporting Young Minds to Thrive"],
    metaDescription:
      "Is your child struggling emotionally or behaviourally? Our specialist child therapists offer compassionate online child therapy in the UK. Book a session today.",
    heroSubtitle:
      "Children experience real emotional pain, and they deserve real support. Our specialist child therapists create a safe, nurturing space where young people can express themselves and begin to heal.",
    heroImage:
      "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=1600&q=80",
    sectionImage:
      "https://images.unsplash.com/photo-1484820540004-14229fe36ca4?auto=format&fit=crop&w=800&q=80",
    color: "#4a90b8",
    whatIs: {
      heading: "What Is Child Therapy?",
      body: [
        "Child therapy is a specialised form of psychological support designed to help children and young people aged 5 to 17 navigate emotional, behavioural, and developmental challenges. Unlike adult therapy, child therapy uses age-appropriate methods, including play, art, storytelling, and structured conversation, to help children express feelings they may not yet have the words to describe.",
        "Children face a wide range of difficulties: family breakdown, bereavement, school-related stress, friendship problems, trauma, neurodevelopmental conditions, and more. These experiences can affect a child's mood, behaviour, development, and sense of self in profound ways. Without the right support, difficulties that begin in childhood can persist well into adulthood.",
        "A skilled child therapist works collaboratively with the child and, where appropriate, with parents and carers. The goal is not only to resolve immediate difficulties, but to equip children with emotional skills and resilience that will serve them throughout their lives.",
      ],
    },
    symptoms: {
      heading: "Signs Your Child May Benefit from Therapy",
      items: [
        "Sudden changes in behaviour, mood, or school performance",
        "Persistent sadness, withdrawal, or loss of interest in activities",
        "Excessive worry, fears, or separation anxiety",
        "Difficulty managing anger or frequent emotional outbursts",
        "Sleep problems, nightmares, or bedwetting (regression)",
        "Social difficulties, bullying, or problems with friendships",
        "Low self-esteem or negative self-talk",
        "Reactions to family changes such as divorce, bereavement, or a new sibling",
      ],
    },
    howWeHelp: {
      heading: "Our Approach to Child Therapy",
      body: [
        "At MindTherapy Counselling, our child therapists are specially trained to work with young people in ways that feel natural, engaging, and safe. We understand that children communicate differently to adults, and our approach reflects that.",
        "For younger children, we use play therapy and creative arts approaches that allow them to explore and process emotions through imaginative play, drawing, and storytelling. These methods are grounded in research and are particularly effective for children who struggle to articulate their inner experiences verbally.",
        "For older children and adolescents, we offer Cognitive Behavioural Therapy (CBT), mindfulness-based techniques, and solution-focused approaches tailored to their developmental stage. We work at the young person's pace, building trust before exploring more challenging material.",
        "We also involve parents and carers as part of the therapeutic process. With the child's consent, we provide guidance to families on how to support their child at home, ensuring that progress made in sessions is reinforced in everyday life.",
      ],
    },
    faq: [
      {
        q: "At what age can children start therapy?",
        a: "Our therapists work with children from age 5 upwards. For younger children, sessions are heavily play-based. For teenagers, approaches are more similar to adult therapy, adapted to their developmental needs.",
      },
      {
        q: "Do I need to be present during my child's sessions?",
        a: "For younger children, a parent or carer may be present at the start of sessions. For older children and teens, one-to-one sessions are often more effective. Your therapist will discuss the best approach for your child's age and situation.",
      },
      {
        q: "What if my child refuses to talk?",
        a: "This is very common, especially at first. Our therapists are skilled at building trust with reluctant young people. Play-based and creative methods mean children don't have to talk directly about their problems to make progress.",
      },
      {
        q: "Will you tell me everything my child says in sessions?",
        a: "Confidentiality is important for building trust with young people. Your therapist will explain what is kept confidential and what will be shared. The exception is if there is a safeguarding concern, in which case your therapist has a duty to act.",
      },
      {
        q: "How many sessions will my child need?",
        a: "This depends on the nature and severity of the difficulty. Some children benefit from short-term work (6 to 10 sessions), while others need longer-term support. Your therapist will review progress regularly and keep you informed.",
      },
    ],
  },
  {
    slug: "adult-therapy",
    title: "Adult Therapy",
    filterBranch: "Adult Therapy",
    headline: ["Online Adult Therapy", "A Space That's Entirely Yours"],
    metaDescription:
      "Navigate life's challenges with professional online adult therapy in the UK. Our experienced therapists offer compassionate, confidential support. Book today.",
    heroSubtitle:
      "Adult life is full of pressures, transitions, and moments that can feel overwhelming. Therapy offers a confidential space to understand yourself more deeply, process difficult experiences, and move forward with greater clarity.",
    heroImage:
      "https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1600&q=80",
    sectionImage:
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=800&q=80",
    color: "#5c7a6e",
    whatIs: {
      heading: "What Is Adult Therapy?",
      body: [
        "Adult therapy is a broad term for professional psychological support tailored to the needs, experiences, and challenges of adulthood. It encompasses a wide range of therapeutic approaches, from structured, goal-focused models like Cognitive Behavioural Therapy (CBT) to more exploratory approaches such as psychodynamic or person-centred therapy.",
        "Adults seek therapy for many different reasons. Some come with a specific diagnosis, such as anxiety or depression. Others are navigating a difficult life transition, processing grief or trauma, struggling in relationships, or simply feeling stuck. There is no threshold of suffering you must reach before therapy becomes appropriate; many people come to therapy simply to better understand themselves and live more fully.",
        "Therapy for adults is not a one-size-fits-all process. Your therapist will work with you to understand your unique history, goals, and preferences, and will draw on a range of evidence-based methods to create a therapeutic approach that genuinely fits you.",
      ],
    },
    symptoms: {
      heading: "Reasons Adults Seek Therapy",
      items: [
        "Persistent stress, burnout, or difficulty coping with daily life",
        "Anxiety, low mood, or unexplained emotional numbness",
        "Grief, loss, or significant life changes such as divorce or redundancy",
        "Relationship difficulties, whether romantic, family, or professional",
        "Low self-esteem, self-criticism, or a pervasive sense of not being good enough",
        "Past trauma or difficult childhood experiences affecting present life",
        "A sense of disconnection, lack of purpose, or feeling lost",
        "Wanting to understand patterns of behaviour that keep repeating",
      ],
    },
    howWeHelp: {
      heading: "How Adult Therapy Works at MindTherapy",
      body: [
        "At MindTherapy Counselling, we believe that effective therapy begins with a strong therapeutic relationship. Before anything else, your therapist will take time to understand who you are, what you're going through, and what you're hoping to achieve. There is no rush, and no judgement.",
        "We offer a wide range of therapeutic modalities, including Cognitive Behavioural Therapy (CBT), psychodynamic therapy, person-centred counselling, EMDR for trauma, acceptance and commitment therapy (ACT), and mindfulness-based approaches. Your therapist will discuss which approach or combination of approaches is most likely to be helpful for your specific situation.",
        "We recognise that many adults have complex histories and needs that don't fit neatly into a single category. Our therapists are experienced in working with co-occurring conditions such as anxiety and depression together, trauma alongside relationship difficulties, or neurodiversity alongside workplace stress.",
        "All sessions are held online, offering flexibility around work, family, and personal commitments. You don't need to take time off or commute; you simply need a quiet space and a reliable internet connection. Many of our clients find this convenience significantly reduces the barriers to consistent attendance.",
      ],
    },
    faq: [
      {
        q: "I've never tried therapy before. What should I expect from the first session?",
        a: "The first session is largely an opportunity for your therapist to understand your situation and for you to get a feel for whether the therapist is a good fit. There is no pressure to share more than you're comfortable with. You're welcome to ask questions too.",
      },
      {
        q: "How do I know which type of therapy is right for me?",
        a: "Your therapist will guide you through this. During your initial session, they will explore your history, goals, and preferences and explain which approaches might be most helpful. Many therapists are integrative, meaning they draw on multiple models depending on what you need.",
      },
      {
        q: "Can therapy help even if I'm not in crisis?",
        a: "Absolutely. Many people come to therapy not because they're struggling to function, but because they want to understand themselves better, break unhelpful patterns, or navigate a transition. You don't need to be at a low point to benefit.",
      },
      {
        q: "How long does adult therapy typically take?",
        a: "It varies widely. Some people find short-term therapy of 6 to 12 sessions very effective. Others choose to engage in longer-term work, particularly when exploring deeper patterns or complex trauma. You and your therapist will review progress regularly and adjust accordingly.",
      },
      {
        q: "Is everything I say completely confidential?",
        a: "Yes. Confidentiality is a cornerstone of the therapeutic relationship. The only exceptions are standard safeguarding situations involving serious risk of harm, which your therapist will explain clearly at the start of your work together.",
      },
    ],
  },
];

export const getLandingPage = (slug) =>
  LANDING_PAGES.find((p) => p.slug === slug) || null;
